import { useState, useEffect } from 'react'
import StoreSearch from '../components/StoreSearch'
import SVG from '../components/SVG'
import { useForm } from 'react-hook-form'
import StoresMap from '../components/StoresMap'
import { useRouter } from 'next/router'
import { setPageViewData } from '../utils/analytics'
import AEMContent, { AEMPageType } from '../components/AEMContent'
import getConfig from 'next/config'
import Head from 'next/head'
import Image from 'next/image'
import Link from 'next/link'

const Stores = ({ term, storeLocatorTemplate, seoMeta, states }) => {
  const [searchTerm, setSearchTerm] = useState(term)
  const [findLocation, setFindLocation] = useState(false)
  const [storesList, setStoresList] = useState(false)
  const [searchData, setSearchData] = useState(null)
  const [selectedStoreId, setSelectedStoreId] = useState(null)
  const [showStores, setShowStore] = useState(false)
  const [showStatesList, setShowStatesList] = useState(true)

  const router = useRouter()
  const { register, handleSubmit } = useForm({
    defaultValues: {
      'location-search': ''
    }
  })

  useEffect(() => {
    setPageViewData('store-locator', 'Store', 'Store')
  }, [])

  useEffect(() => {
    if (findLocation || searchTerm != null) {
      setStoresList(true)
    }
  }, [searchTerm, findLocation])

  const onSubmit = (data) => {
    setSearchTerm(data['location-search'])
    router.push({ query: `${searchTerm || ''}` }, undefined, { shallow: true })
    setFindLocation(false)
  }

  const renderUseMyLocation = () => {
    return (
      <button className="stores-initial-search__use-my-location" onClick={() => setFindLocation(true)}>
        <SVG name="icon-location" label="icon-location" size="base" />
        <div className="stores-initial-search__location__text">Use my location</div>
      </button>
    )
  }

  return (
    <div className={`stores ${!!storesList && 'stores--stores-list'}`}>
      <Head>
        <title>{seoMeta?.title}</title>
        <meta name="description" content={seoMeta?.description} />
      </Head>
      <div className="stores__initial" hidden={!!storesList}>
        <div className="stores__initial-content">
          <div className="stores__initial-image">
            <Image src="/images/store.png" alt="Store" width={391} height={186} />
          </div>
          <div className="stores__initial-search">
            <div id="main-content" className="stores-initial-search">
              <div className="stores-initial-search__container">
                <div className="stores-initial-search__main-header">
                  <h2>find a store</h2>
                </div>
                <p>Enter zipcode or city and state</p>
                <form
                  className="stores-initial-search__header"
                  onSubmit={handleSubmit(onSubmit)}
                >
                  <div className="input-wrapper">
                    <input
                      {...register('location-search')}
                      id="location-search"
                      type="text"
                      aria-label="Enter zipcode or city and state"
                      placeholder="zipcode or city and state"
                    />
                  </div>
                  <div className="stores-initial-search__header__button">
                    <button type="submit">GO</button>
                  </div>
                </form>
                {renderUseMyLocation()}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="stores__container" hidden={!storesList}>
        <div className='stores__container-search-and-map'>
          {storesList && (
            <div className={`stores__container-search ${showStores ? 'stores__container-search--open' : 'stores__container-search--closed'}`}>
              <StoreSearch
                selectedStoreId={selectedStoreId}
                onChange={setSearchData}
                isModal={false}
                searchText={searchTerm}
                findLocation={findLocation}
              />
            </div>
          )}
          <div className="stores__container-map">
            <StoresMap
              selectedStoreId={selectedStoreId}
              onStoreClick={setSelectedStoreId}
              center={searchData?.origin}
              stores={searchData?.stores}
            />
          </div>
        </div>
        <div className='stores__container-toggle' onClick={() => {
          setShowStore(!showStores)
        }}>
          {showStores ? 'Show Map' : 'Show Stores'}
        </div>
      </div>
      <div className="stores-initial-search__separator"></div>
      <div className="stores-initial-search__view-by-state">
        <button
          className="stores-initial-search__view-by-state-toggle"
          onClick={() => setShowStatesList(!showStatesList)}
        >
          <span>view locations by state</span>
          <span className={showStatesList ? 'stores-initial-search__view-by-state-icon--up' : 'stores-initial-search__view-by-state-icon--down'}>
            <SVG
              name="arrow-down"
              label={showStatesList ? 'Collapse' : 'Expand'}
              size="base"
            />
          </span>
        </button>
        {showStatesList && (
          <>
            <p className="stores-initial-search__states-label">Select by state</p>
            <div className="stores-initial-search__states-list">
              {states?.map((state) => (
              <Link href={`/locations/${state.short_name}`} key={state.short_name} className="stores-initial-search__states-list-item">
                  {state.long_name}
              </Link>
              ))}
            </div>
          </>
        )}
      </div>
      <div className={'stores__aem-content'}>
        <AEMContent template={storeLocatorTemplate} page={AEMPageType.stores} />
      </div>
    </div>
  )
}

export default Stores

export const getServerSideProps = async ({ query }) => {
  const { publicRuntimeConfig } = getConfig()
  const res = await fetch(
    `${publicRuntimeConfig.APP_ENDPOINT}/api/stores/${publicRuntimeConfig.ENV}/store-locator-template?popkey=${publicRuntimeConfig.POP_KEY}`,
    {
      headers: {
        'X-Pop-App-Token': publicRuntimeConfig.POP_TOKEN_WEB
      }
    }
  )
  const data = await res.json()

  const storeLocatorData = data.data.store_locator || []

  const responsiveGrid = storeLocatorData.find(item => item.type === 'responsive-grid')
  const seoMetaBlock = storeLocatorData.find(item => item.type === 'seometablock')

  const statesUrl = `${publicRuntimeConfig.APP_ENDPOINT}/api/stores/${publicRuntimeConfig.ENV}/states?popkey=${publicRuntimeConfig.POP_KEY}`
  const statesRes = await fetch(statesUrl)
  const states = await statesRes.json()

  return {
    props: {
      term: query.s || null,
      storeLocatorTemplate: responsiveGrid.template || [],
      seoMeta: seoMetaBlock || {
        title: 'Stores',
        description: 'Find a store near you'
      },
      states: states
    }
  }
}
